import React, { useMemo } from "react"
import { useDispatch } from "react-redux"
import InfiniteScroll from "react-infinite-scroll-component"
import Stack from "@mui/material/Stack"
import { FEED_CARD, POPULAR_ITEMS, PRO_BANNER, QUICK_CREATE_CARD, FEED_LAYOUT_RULES, normalizeLayout } from "constants/feed"
import { increaseFeedPage } from "appRedux/slices/feedSlice"
import { useLazyGetFeedQuery } from "appRedux/slices/feedApiSlice"
import { useAppSelector } from "appRedux/hooks"
import Loading from "components/atoms/Loading"
import ProBanner from "components/molecules/ProBanner"
import { feedCardMap } from "components/organisms/FeedCard/utils"
import PopularItemsSection from "components/organisms/PopularItemsSection"
import QuickCreateCard from "components/organisms/QuickCreateCard"
import LoadingPlaceholder from "components/templates/FeedsTemplate/LoadingPlaceholder"

type FeedsTemplateProps = {
  isLoading: boolean;
}

export default function FeedsTemplate({ isLoading }: FeedsTemplateProps) {
  const feed = useAppSelector((state) => state.feed)
  const isPremium = useAppSelector((state) => state.user.profile?.isPremium)

  const [triggerGetFeed] = useLazyGetFeedQuery()

  const dispatch = useDispatch()

  const activeLayout = useMemo(() => {
    const rule = FEED_LAYOUT_RULES.find((r) => r.when(feed.feeds))
    if (!rule) return []
    return normalizeLayout(rule.layout, {
      removeTypes: isPremium ? [PRO_BANNER] : []
    })
  }, [feed.feeds, isPremium])

  const lastLayoutEndIndex = useMemo(() => {
    const feedCardEnds = activeLayout.flatMap(item => item.type === FEED_CARD ? [item.end] : [])
    if (feedCardEnds.length === 0) return 0
    return Math.max(...feedCardEnds)
  }, [activeLayout])

  function fetchMoreFeed() {
    if (!feed.hasMore) return
    triggerGetFeed(feed.page + 1)
    dispatch(increaseFeedPage())
  }

  return (
    isLoading ? <LoadingPlaceholder/> :
      <InfiniteScroll
        dataLength={feed.feeds.length}
        next={fetchMoreFeed}
        hasMore={feed.hasMore}
        loader={<Loading/>}
        scrollThreshold={"20px"}
      >
        <Stack flexGrow={1} height="100%" paddingTop="16px">
          {activeLayout.map((item, index) => {
            const content = (() => {
              switch (item.type) {
                case FEED_CARD:
                  return feed.feeds
                    .slice(item.start, item.end)
                    .map((record, i) => (
                      <React.Fragment key={`feed-${index}-${i}`}>
                        {feedCardMap[record.type]({ feed: record, index: i })}
                      </React.Fragment>
                    ))
                case PRO_BANNER:
                  return <ProBanner key={`banner-${index}`}/>
                case POPULAR_ITEMS:
                  return <PopularItemsSection key={`suggest-${index}`}/>
                case QUICK_CREATE_CARD:
                  return <QuickCreateCard key={`quick-create-${index}`}/>
                default:
                  return null
              }
            })()

            return (
              <Stack key={`layout-${index}`} px={item.type === POPULAR_ITEMS ? 0 : 2}>
                {content}
              </Stack>
            )
          })}

          <Stack px={2}>
            {feed.feeds.slice(lastLayoutEndIndex).map((record, i) => (
              <React.Fragment key={`rest-${i}`}>
                {feedCardMap[record.type]({ feed: record, index: i })}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      </InfiniteScroll>
  )
}
